import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  FolderOpen, 
  Clock, 
  TrendingUp, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Search,
  Filter,
  UserPlus,
  FileText,
  UserCheck,
  UserX,
  XCircle,
  CheckCircle,
  AlertCircle,
  Activity,
  Calendar,
  X
} from 'lucide-react';
import { projectAPI, timesheetAPI, excelAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { downloadExcelFile, getCurrentDateRange } from '../utils/excelUtils';
import toast from 'react-hot-toast';

const ManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [isLoadingEmployeeDetails, setIsLoadingEmployeeDetails] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [timesheetSearchTerm, setTimesheetSearchTerm] = useState('');
  const [timesheetIdSearch, setTimesheetIdSearch] = useState('');
  const [timesheetStatusFilter, setTimesheetStatusFilter] = useState('ALL');
  const [timesheetStartDate, setTimesheetStartDate] = useState('');
  const [timesheetEndDate, setTimesheetEndDate] = useState('');
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [showTimesheetDetails, setShowTimesheetDetails] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const queryClient = useQueryClient();

  // Excel Export Functions
  const handleExportTimesheetsToExcel = async () => {
    try {
      const { startDate, endDate } = getCurrentDateRange();
      const response = await excelAPI.exportTimesheetsManager(startDate, endDate);
      downloadExcelFile(response.data, `manager_timesheet_report_${startDate}_to_${endDate}.xlsx`);
      toast.success('Timesheet report exported successfully!');
    } catch (error) {
      console.error('Error exporting timesheets:', error);
      toast.error('Failed to export timesheet report: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleExportEmployeesToExcel = async () => {
    try {
      const response = await excelAPI.exportEmployeesManager();
      downloadExcelFile(response.data, 'manager_employee_report.xlsx');
      toast.success('Employee report exported successfully!');
    } catch (error) {
      console.error('Error exporting employees:', error);
      toast.error('Failed to export employee report: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleExportProjectsToExcel = async () => {
    try {
      const response = await excelAPI.exportProjectsManager();
      downloadExcelFile(response.data, 'manager_project_report.xlsx');
      toast.success('Project report exported successfully!');
    } catch (error) {
      console.error('Error exporting projects:', error);
      toast.error('Failed to export project report: ' + (error.response?.data?.message || error.message));
    }
  };

  // Optimized: Single endpoint for dashboard stats (counts only)
  const { data: dashboardStats, isLoading: statsLoading } = useQuery(
    'manager-dashboard-stats',
    () => projectAPI.getManagerDashboardStats(),
    {
      select: (response) => response.data,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );

  // Fetch all employees (needed for tabs, but not blocking dashboard)
  const { data: employees = [], isLoading: employeesLoading, refetch: refetchEmployees } = useQuery(
    'manager-employees',
    () => projectAPI.getAllEmployeesForManager(),
    {
      select: (response) => response.data || [],
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: 60_000,
      keepPreviousData: true,
    }
  );

  // Fetch all projects (needed for tabs, but not blocking dashboard)
  const { data: projects = [], isLoading: projectsLoading, refetch: refetchProjects } = useQuery(
    'manager-projects',
    () => projectAPI.getManagerProjects(),
    {
      select: (response) => response.data || [],
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: 60_000,
      keepPreviousData: true,
    }
  );

  // Fetch all timesheets for complete review (needed for tabs, but not blocking dashboard)
  const { data: timesheets, isLoading: timesheetsLoading, refetch: refetchTimesheets } = useQuery(
    'manager-all-timesheets',
    () => projectAPI.getAllManagerTimesheets(),
    {
      select: (response) => response.data?.timesheets || [],
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
      refetchIntervalInBackground: true,
      staleTime: 1 * 60 * 1000, // Cache for 1 minute
      cacheTime: 5 * 60 * 1000,
      onSuccess: () => {
        setLastUpdateTime(new Date());
      }
    }
  );

  // Update employee mutation
  const updateEmployeeMutation = useMutation(
    ({ employeeId, employeeData }) => projectAPI.updateAnyEmployee(employeeId, employeeData),
    {
      onSuccess: () => {
        toast.success('Employee updated successfully!');
        setEditingEmployee(null);
        queryClient.invalidateQueries('manager-employees');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update employee');
      },
    }
  );

  // Delete employee mutation
  const deleteEmployeeMutation = useMutation(
    (employeeId) => {
      console.log('DEBUG: Frontend - Attempting to delete employee with ID:', employeeId);
      return projectAPI.deleteManagerEmployee(employeeId);
    },
    {
      onSuccess: (data) => {
        console.log('DEBUG: Frontend - Delete employee success:', data);
        toast.success('Employee deactivated successfully!');
        setEmployeeToDelete(null);
        queryClient.invalidateQueries('manager-employees');
      },
      onError: (error) => {
        console.log('DEBUG: Frontend - Delete employee error:', error);
        console.log('DEBUG: Frontend - Error response:', error.response);
        console.log('DEBUG: Frontend - Error response data:', error.response?.data);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to deactivate employee';
        console.log('DEBUG: Frontend - Final error message:', errorMessage);
        toast.error(errorMessage);
      },
    }
  );

  // Update project mutation
  const updateProjectMutation = useMutation(
    ({ projectId, projectData }) => projectAPI.updateAnyProject(projectId, projectData),
    {
      onSuccess: () => {
        toast.success('Project updated successfully!');
        setEditingProject(null);
        queryClient.invalidateQueries('manager-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update project');
      },
    }
  );

  // Delete project mutation
  const deleteProjectMutation = useMutation(
    (projectId) => projectAPI.deactivateAnyProject(projectId),
    {
      onSuccess: () => {
        toast.success('Project deactivated successfully!');
        setProjectToDelete(null);
        queryClient.invalidateQueries('manager-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to deactivate project');
      },
    }
  );

  // Assign employee to project mutation
  const assignEmployeeMutation = useMutation(
    ({ projectId, employeeId }) => projectAPI.assignManagerEmployeeToProject(projectId, employeeId),
    {
      onSuccess: () => {
        toast.success('Employee assigned to project successfully!');
        queryClient.invalidateQueries('manager-projects');
        queryClient.invalidateQueries('manager-employees');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to assign employee to project';
        toast.error(errorMessage);
      },
    }
  );

  // Remove employee from project mutation
  const removeEmployeeMutation = useMutation(
    ({ projectId, employeeId }) => projectAPI.removeManagerEmployeeFromProject(projectId, employeeId),
    {
      onSuccess: () => {
        toast.success('Employee removed from project successfully!');
        queryClient.invalidateQueries('manager-projects');
        queryClient.invalidateQueries('manager-employees');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove employee from project');
      },
    }
  );

  // Timesheet approval mutation
  const approveTimesheetMutation = useMutation(
    (timesheetId) => projectAPI.approveManagerTimesheet(timesheetId),
    {
      onSuccess: () => {
        toast.success('Timesheet approved successfully');
        queryClient.invalidateQueries('manager-all-timesheets');
        setShowTimesheetDetails(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  // Timesheet rejection mutation
  const rejectTimesheetMutation = useMutation(
    ({ timesheetId, reason }) => projectAPI.rejectManagerTimesheet(timesheetId, reason),
    {
      onSuccess: () => {
        toast.success('Timesheet rejected successfully');
        queryClient.invalidateQueries('manager-all-timesheets');
        setShowTimesheetDetails(false);
        setShowRejectionModal(false);
        setRejectionReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );



  // Calculate statistics from optimized endpoint
  const stats = {
    totalEmployees: dashboardStats?.totalEmployees || 0,
    submittedTimesheets: dashboardStats?.submittedTimesheets || 0,
    totalProjects: dashboardStats?.totalProjects || 0,
    approvedTimesheets: dashboardStats?.approvedTimesheets || 0,
  };

  // Filter data based on search term
  const filteredEmployees = employees.filter(employee =>
    employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProjects = projects.filter(project =>
    project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Timesheet filtering and helper functions
  const filteredTimesheets = timesheets?.filter(timesheet => {
    // Status filter
    const matchesStatus = timesheetStatusFilter === 'ALL' || timesheet.status === timesheetStatusFilter;
    if (!matchesStatus) {
      return false;
    }
    
    // Check if searching by timesheet ID
    const hasTimesheetIdSearch = timesheetIdSearch && timesheetIdSearch.trim() !== '';
    
    // If searching by ID, check ID match first
    if (hasTimesheetIdSearch) {
      const searchId = String(timesheetIdSearch).trim();
      const timesheetId = timesheet.id ? String(timesheet.id) : '';
      if (!timesheetId.includes(searchId)) {
        return false; // ID doesn't match, exclude this timesheet
      }
      // ID matches, continue with date filter
    } else {
      // Not searching by ID, check employee search
      const hasEmployeeSearch = timesheetSearchTerm && timesheetSearchTerm.trim() !== '';
      if (hasEmployeeSearch) {
        const searchLower = timesheetSearchTerm.toLowerCase();
        const matchesEmployee = 
          timesheet.employee?.firstName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.lastName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.email?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.username?.toLowerCase().includes(searchLower);
        if (!matchesEmployee) {
          return false; // Employee search doesn't match
        }
      }
    }
    
    // Date range filtering
    let matchesDateRange = true;
    if (timesheetStartDate || timesheetEndDate) {
      const timesheetStart = new Date(timesheet.startDate || timesheet.weekStartDate);
      const timesheetEnd = new Date(timesheet.endDate || timesheet.weekEndDate);
      const filterStart = timesheetStartDate ? new Date(timesheetStartDate) : null;
      const filterEnd = timesheetEndDate ? new Date(timesheetEndDate) : null;
      
      if (isNaN(timesheetStart.getTime()) || isNaN(timesheetEnd.getTime())) {
        matchesDateRange = false;
      } else if (filterStart && filterEnd) {
        matchesDateRange = timesheetStart <= filterEnd && timesheetEnd >= filterStart;
      } else if (filterStart) {
        matchesDateRange = timesheetEnd >= filterStart;
      } else if (filterEnd) {
        matchesDateRange = timesheetStart <= filterEnd;
      }
    }
    
    return matchesDateRange;
  }) || [];

  // Sort timesheets by last updated
  const sortedTimesheets = [...filteredTimesheets].sort((a, b) => {
    const aValue = new Date(a.updatedAt || a.createdAt);
    const bValue = new Date(b.updatedAt || b.createdAt);
    return bValue - aValue; // Most recent first
  });



  const handleDeleteEmployee = (employeeId, employeeName) => {
    if (window.confirm(`Are you sure you want to deactivate "${employeeName}"?`)) {
      deleteEmployeeMutation.mutate(employeeId);
    }
  };

  const handleDeleteProject = (projectId, projectName) => {
    if (window.confirm(`Are you sure you want to deactivate "${projectName}"?`)) {
      deleteProjectMutation.mutate(projectId);
    }
  };

  const handleAssignEmployee = (project) => {
    setSelectedProject(project);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = (employeeId) => {
    assignEmployeeMutation.mutate({
      projectId: selectedProject.id,
      employeeId: employeeId
    });
    setShowAssignModal(false);
    setSelectedProject(null);
  };

  const handleViewEmployeeDetails = async (employee) => {
    setIsLoadingEmployeeDetails(true);
    setSelectedEmployee(employee);
    
    try {
      // Fetch detailed employee information
      const response = await projectAPI.getManagerEmployeeDetails(employee.id);
      setEmployeeDetails(response.data);
      setShowEmployeeDetails(true);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      toast.error('Failed to fetch employee details');
    } finally {
      setIsLoadingEmployeeDetails(false);
    }
  };

  // Timesheet handler functions
  const handleTimesheetApproval = (timesheetId) => {
    approveTimesheetMutation.mutate(timesheetId);
  };

  const handleTimesheetRejection = (timesheetId) => {
    setSelectedTimesheet(timesheets?.find(t => t.id === timesheetId));
    setShowRejectionModal(true);
  };


  const handleRejectConfirm = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    if (selectedTimesheet) {
      rejectTimesheetMutation.mutate({
        timesheetId: selectedTimesheet.id,
        reason: rejectionReason.trim()
      });
    }
  };

  // Timesheet status helper functions
  const getTimesheetStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Clock className="w-4 h-4 text-gray-500" />;
      case 'SUBMITTED':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'PENDING':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'REUPDATED':
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTimesheetStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';
      case 'REUPDATED':
        return 'bg-blue-100 text-blue-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };



  // Show loading only for critical stats, allow progressive rendering
  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manager Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.firstName} {user?.lastName}</p>
        </div>
      </motion.div>

      {/* Statistics Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEmployees}</p>
            </div>
          </div>
        </div>

        <motion.div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/manager/timesheets?status=SUBMITTED')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Submitted Timesheets</p>
                <p className="text-2xl font-bold text-gray-900">{stats.submittedTimesheets}</p>
              </div>
            </div>
            <div className="text-xs text-gray-400">
              Click to view
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/manager/timesheets?status=APPROVED')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved Timesheets</p>
                <p className="text-2xl font-bold text-gray-900">{stats.approvedTimesheets}</p>
              </div>
            </div>
            <div className="text-xs text-gray-400">
              Click to view
            </div>
          </div>
        </motion.div>

      </motion.div>

      {/* Excel Export Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Export Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportTimesheetsToExcel}
            className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors text-left"
          >
            <FileText className="w-8 h-8 text-green-600 mb-2" />
            <h3 className="font-medium text-gray-900">Export Timesheets</h3>
            <p className="text-sm text-gray-500">Download timesheet data as Excel</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportEmployeesToExcel}
            className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
          >
            <Users className="w-8 h-8 text-blue-600 mb-2" />
            <h3 className="font-medium text-gray-900">Export Employees</h3>
            <p className="text-sm text-gray-500">Download employee data as Excel</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportProjectsToExcel}
            className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
          >
            <FolderOpen className="w-8 h-8 text-purple-600 mb-2" />
            <h3 className="font-medium text-gray-900">Export Projects</h3>
            <p className="text-sm text-gray-500">Download project data as Excel</p>
          </motion.button>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('employees')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'employees'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Employees
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'projects'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Projects
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Employees</h3>
                <div className="space-y-3">
                  {employees.slice(0, 5).map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {employee.firstName} {employee.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{employee.email}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        employee.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Projects</h3>
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{project.name}</p>
                        <p className="text-sm text-gray-500">{project.description}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        project.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {project.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'employees' && (
            <div className="space-y-4">
              {/* Employee Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Users className="w-8 h-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Total Employees</p>
                      <p className="text-2xl font-bold text-blue-900">{filteredEmployees.length}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <UserCheck className="w-8 h-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">Active Employees</p>
                      <p className="text-2xl font-bold text-green-900">
                        {filteredEmployees.filter(emp => emp.isActive).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <UserX className="w-8 h-8 text-red-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-600">Inactive Employees</p>
                      <p className="text-2xl font-bold text-red-900">
                        {filteredEmployees.filter(emp => !emp.isActive).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Employee List - Same format as Admin/SuperAdmin */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-lg shadow-sm"
              >
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">
                    All Employees ({filteredEmployees.length})
                  </h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {filteredEmployees.map((employee, index) => (
                    <motion.div
                      key={employee.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </h3>
                            <p className="text-sm text-gray-500">{employee.email}</p>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-500">
                                ID: {employee.id}
                              </span>
                              <span className="text-xs text-gray-500">
                                Username: {employee.username}
                              </span>
                              {employee.managerFullName && (
                                <span className="text-xs text-gray-500">
                                  Manager: {employee.managerFullName}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                employee.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {employee.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewEmployeeDetails(employee)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingEmployee(employee)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Edit Employee"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEmployee(employee.id, `${employee.firstName} ${employee.lastName}`)}
                            disabled={deleteEmployeeMutation.isLoading}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Deactivate Employee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                {filteredEmployees.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No employees found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm 
                        ? 'Try adjusting your search criteria.'
                        : 'No employees have been added yet.'
                      }
                    </p>
                  </div>
                )}
              </motion.div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="space-y-4">
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{project.name}</h3>
                    <p className="text-sm text-gray-500">{project.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Created {format(new Date(project.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {project.isActive ? 'Active' : 'Inactive'}
                    </span>
                    
                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleAssignEmployee(project)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                        title="Assign Employee"
                      >
                        <UserPlus className="w-4 h-4" />
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setEditingProject(project)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit Project"
                      >
                        <Edit className="w-4 h-4" />
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDeleteProject(project.id, project.name)}
                        disabled={deleteProjectMutation.isLoading}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Deactivate Project"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

        </div>
      </motion.div>

      {/* Edit Employee Modal */}
      {editingEmployee && (
        <EditEmployeeModal
          employee={editingEmployee}
          onClose={() => setEditingEmployee(null)}
          onSubmit={(employeeData) => updateEmployeeMutation.mutate({
            employeeId: editingEmployee.id,
            employeeData
          })}
          isLoading={updateEmployeeMutation.isLoading}
        />
      )}

      {/* Employee Details Modal */}
      {showEmployeeDetails && selectedEmployee && (
        <EmployeeDetailsModal
          employee={employeeDetails || selectedEmployee}
          onClose={() => {
            setShowEmployeeDetails(false);
            setSelectedEmployee(null);
            setEmployeeDetails(null);
          }}
          isLoading={isLoadingEmployeeDetails}
        />
      )}

      {/* Assign Employee Modal */}
      {showAssignModal && selectedProject && (
        <AssignEmployeeModal
          project={selectedProject}
          employees={employees}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedProject(null);
          }}
          onSubmit={handleAssignSubmit}
          isLoading={assignEmployeeMutation.isLoading}
        />
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSubmit={(projectData) => updateProjectMutation.mutate({
            projectId: editingProject.id,
            projectData
          })}
          isLoading={updateProjectMutation.isLoading}
        />
      )}

      {/* Timesheet Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Timesheet</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this timesheet.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={3}
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={rejectTimesheetMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {rejectTimesheetMutation.isLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timesheet Details Modal */}
      {showTimesheetDetails && selectedTimesheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Timesheet Details</h3>
              <button
                onClick={() => {
                  setShowTimesheetDetails(false);
                  setSelectedTimesheet(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Employee Information */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Employee Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedTimesheet.employee?.firstName} {selectedTimesheet.employee?.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-sm font-medium text-gray-900">{selectedTimesheet.employee?.email}</p>
                </div>
              </div>
            </div>

            {/* Timesheet Information */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Timesheet Information</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Period</p>
                  <p className="text-sm font-medium text-gray-900">
                    {format(new Date(selectedTimesheet.startDate), 'MMM dd, yyyy')} - {format(new Date(selectedTimesheet.endDate), 'MMM dd, yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Hours</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedTimesheet.entries?.reduce((total, entry) => total + (entry.hours || 0), 0) || 0} hours
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTimesheetStatusColor(selectedTimesheet.status)}`}>
                    {getTimesheetStatusIcon(selectedTimesheet.status)}
                    <span className="ml-1">{selectedTimesheet.status}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Time Entries */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Time Entries</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedTimesheet.entries?.map((entry, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {format(new Date(entry.date), 'MMM dd, yyyy')}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {entry.project?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {entry.task || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {entry.hours || 0} hours
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            {['SUBMITTED', 'PENDING', 'REUPDATED'].includes(selectedTimesheet.status) && (
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => handleTimesheetApproval(selectedTimesheet.id)}
                  disabled={approveTimesheetMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {approveTimesheetMutation.isLoading ? 'Approving...' : 'Approve'}
                </button>
                <button
                  onClick={() => handleTimesheetRejection(selectedTimesheet.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Reject
                </button>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

// Edit Employee Modal Component
const EditEmployeeModal = ({ employee, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    email: employee.email,
    firstName: employee.firstName,
    lastName: employee.lastName,
    password: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Employee</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">New Password (optional)</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="form-input"
                placeholder="Leave empty to keep current password"
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? 'Updating...' : 'Update Employee'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Project Modal Component
const EditProjectModal = ({ project, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description,
    isActive: project.isActive,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Project</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Project Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="form-input"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                Active Project
              </label>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? 'Updating...' : 'Update Project'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Assign Employee Modal Component
const AssignEmployeeModal = ({ project, employees, onClose, onSubmit, isLoading }) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedEmployeeId) {
      onSubmit(parseInt(selectedEmployeeId));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Assign Employee to Project</h3>
          <p className="text-sm text-gray-600 mt-1">Assign an employee to "{project.name}"</p>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Employee
            </label>
            <select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Choose an employee...</option>
              {employees
                .filter(emp => emp.isActive)
                .map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.firstName} {employee.lastName} ({employee.email})
                  </option>
                ))
              }
            </select>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedEmployeeId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Assigning...' : 'Assign Employee'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Employee Details Modal Component
const EmployeeDetailsModal = ({ employee, onClose, isLoading }) => {
  if (!employee) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Employee Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        
        <div className="px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Employee Basic Info */}
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-lg font-medium text-white">
                    {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {employee.firstName} {employee.lastName}
                  </h2>
                  <p className="text-gray-600">{employee.email}</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full mt-1 ${
                    employee.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {employee.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Employee Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Employee ID</label>
                    <p className="mt-1 text-sm text-gray-900">{employee.id}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <p className="mt-1 text-sm text-gray-900">{employee.username}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{employee.email}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <p className="mt-1 text-sm text-gray-900">{employee.role?.name || 'EMPLOYEE'}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {employee.managerFullName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Manager</label>
                      <p className="mt-1 text-sm text-gray-900">{employee.managerFullName}</p>
                    </div>
                  )}
                  
                  {employee.createdByFullName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created By</label>
                      <p className="mt-1 text-sm text-gray-900">{employee.createdByFullName}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created At</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {employee.createdAt ? format(new Date(employee.createdAt), 'PPP') : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {employee.updatedAt ? format(new Date(employee.updatedAt), 'PPP') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </motion.div>


    </div>
  );
};

export default ManagerDashboard;